<template>
  <!-- Compact "quick reactions + tenant catalog" picker shown on
       hover-action on a message. Posts the reaction to the server,
       which fans out the WS update to all viewers. -->
  <div
    v-if="open"
    ref="rootEl"
    class="absolute z-30 rounded-lg border border-white/10 bg-[var(--surface-color,#0d0e12)] shadow-xl p-2 min-w-[220px] max-w-[280px]"
    :class="placement === 'right' ? 'right-0' : 'left-0'"
    style="bottom: 100%; margin-bottom: 6px"
    @click.stop
  >
    <!-- Quick row: 6 common Unicode reactions -->
    <div class="flex items-center gap-1 mb-2 pb-2 border-b border-white/5">
      <button
        v-for="e in QUICK_REACTIONS"
        :key="e.char"
        type="button"
        class="group/qr w-7 h-7 rounded-md hover:bg-white/[0.08] flex items-center justify-center"
        :title="e.label"
        @click="pickUnicode(e.char)"
      >
        <img
          :src="twemojiUrlFor(e.char)"
          :alt="e.char"
          class="w-[18px] h-[18px] object-contain"
          loading="lazy"
          draggable="false"
        />
      </button>
    </div>

    <!-- Tenant catalog row (most-used, top 8) -->
    <div v-if="catalogTop.length" class="grid grid-cols-8 gap-1">
      <button
        v-for="e in catalogTop"
        :key="e.name"
        type="button"
        class="w-7 h-7 rounded-md hover:bg-white/[0.08] flex items-center justify-center"
        :title="`:${e.name}:`"
        @click="pickCustom(e.name)"
      >
        <img
          :src="e.url"
          :alt="e.name"
          class="max-w-[20px] max-h-[20px] object-contain"
          loading="lazy"
          draggable="false"
        />
      </button>
    </div>

    <div v-else class="text-[10px] text-text-dim px-1">
      {{ $t('chat.reactions.no_custom') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useCustomEmoji } from '~/composables/chat/useCustomEmoji'
import { splitUnicodeEmoji } from '~/utils/twemoji'

const props = defineProps<{
  modelValue: boolean
  messageId: string
  placement?: 'left' | 'right'
}>()
const emit = defineEmits<{
  'update:modelValue': [v: boolean]
  picked: [{ emoji: string }]
}>()

const open = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v)
})

const rootEl = ref<HTMLDivElement | null>(null)

const QUICK_REACTIONS = [
  { char: '👍', label: 'thumbs up' },
  { char: '❤️', label: 'heart' },
  { char: '🔥', label: 'fire' },
  { char: '🎉', label: 'party' },
  { char: '😂', label: 'laughing' },
  { char: '🙏', label: 'praying' }
]

const { catalog, ensureLoaded } = useCustomEmoji()
onMounted(() => { ensureLoaded() })

// Top 8 of the tenant catalog by useCount (the loaded catalog is
// already pre-sorted by the index.get endpoint, but resort defensively
// in case future loaders reorder it).
const catalogTop = computed(() => {
  const all = Array.from(catalog.value.values())
  all.sort((a: any, b: any) => (b.useCount || 0) - (a.useCount || 0))
  return all.slice(0, 8)
})

function twemojiUrlFor(char: string): string {
  const segs = splitUnicodeEmoji(char)
  const first = segs.find(s => s.type === 'unicode_emoji') as any
  return first?.url || ''
}

async function pickUnicode(char: string) {
  await react(char)
}
async function pickCustom(name: string) {
  // Stored as bare shortcode (no colons) — matches reactions endpoint.
  await react(name)
}

async function react(emoji: string) {
  try {
    await $fetch(`/api/chat/messages/${props.messageId}/reactions`, {
      method: 'POST',
      body: { emoji }
    })
    emit('picked', { emoji })
  } catch {
    // WS frame is the source of truth; silent failure is OK because the
    // next event resyncs.
  } finally {
    open.value = false
  }
}

// Click-outside to close — wire only while open.
function onDocClick(ev: MouseEvent) {
  if (!open.value) return
  if (rootEl.value && rootEl.value.contains(ev.target as Node)) return
  open.value = false
}
watch(open, (v) => {
  if (typeof document === 'undefined') return
  if (v) document.addEventListener('mousedown', onDocClick, true)
  else document.removeEventListener('mousedown', onDocClick, true)
})
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('mousedown', onDocClick, true)
  }
})
</script>
